import { yupResolver } from "@hookform/resolvers/yup";
import { Add, Delete } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";

import { Acquirer } from "../../interfaces/acquirer";
import { ApiResponse } from "../../interfaces/paginated";
import { api } from "../../services/api";
import { acquirerSchema } from "../../validators/schemas/acquirer";

type FormData = {
    name: string;
    description: string;
    active: boolean;
    type: string;
    acquirer_meta_data: {
        id?: string;
        key: string;
        value: string;
    }[];
};

interface AcquirerEditModalProps {
    open: boolean;
    onClose: () => void;
    data: Acquirer | null;
    onSuccess?: (data: FormData) => void;
    onError?: (data: FormData, error: string) => void;
}

export function AcquirerEditModal({
    open,
    onClose,
    data,
    onSuccess,
    onError,
}: AcquirerEditModalProps) {
    const {
        handleSubmit,
        control,
        setValue,
        reset,
        formState: { errors, isSubmitting, isValid },
    } = useForm<FormData>({
        mode: "onChange",
        resolver: yupResolver(acquirerSchema),
        defaultValues: {
            type: "gateway",
            active: true,
            acquirer_meta_data: [],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "acquirer_meta_data",
        keyName: "fieldId",
    });

    const handleRemove = (index: number) => {
        remove(index);
    };

    const handleClose = () => {
        onClose();
        reset();
    };

    const onSubmit = async (values: FormData) => {
        try {
            let response;

            if (data?.id) {
                response = await api.put<ApiResponse<null>>(
                    `acquirers/${data.id}`,
                    values
                );
            } else {
                response = await api.post<ApiResponse<null>>(
                    "acquirers",
                    values
                );
            }

            if (!response.data.status) {
                onError?.(values, response.data.message);
                return;
            }

            onSuccess?.(values);
        } catch (error: any) {
            onError?.(values, error?.response?.data?.message || error?.message);
        } finally {
            handleClose();
        }
    };

    useEffect(() => {
        if (open) {
            if (data) {
                setValue("name", data.name);
                setValue("description", data.description);
                setValue("active", data.active);
                setValue("type", data.type);
                setValue("acquirer_meta_data", data.acquirer_meta_data || []);
            }
        } else {
            reset({
                name: "",
                description: "",
                active: true,
                type: "gateway",
                acquirer_meta_data: [],
            });
        }
    }, [data, open]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            disableRestoreFocus
        >
            <DialogTitle variant="h6" component="h2">
                {data
                    ? `Editar Adquirente: ${data.name}`
                    : "Cadastrar Adquirente"}
            </DialogTitle>

            <DialogContent>
                <Box
                    gap={2}
                    display="flex"
                    flexDirection="column"
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    noValidate
                    sx={{ mt: 1 }}
                >
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Nome"
                                size="small"
                                fullWidth
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        )}
                    />

                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Descrição"
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                error={!!errors.description}
                                helperText={errors.description?.message}
                            />
                        )}
                    />

                    <Box display="flex" gap={2} alignItems="center">
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo</InputLabel>
                            <Controller
                                name="type"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        label="Tipo"
                                        error={!!errors.type}
                                        disabled
                                    >
                                        <MenuItem value="gateway">
                                            Gateway de Pagamento (Cartão, PIX)
                                        </MenuItem>
                                        <MenuItem value="billing">
                                            Cobrança (Boleto, Fatura)
                                        </MenuItem>
                                    </Select>
                                )}
                            />
                            <FormHelperText error={!!errors.type}>
                                {errors.type?.message}
                            </FormHelperText>
                        </FormControl>

                        <Controller
                            name="active"
                            control={control}
                            render={({ field: { value, ...field } }) => (
                                <FormControlLabel
                                    control={
                                        <Switch {...field} checked={!!value} />
                                    }
                                    label="Ativo"
                                />
                            )}
                        />
                    </Box>

                    <Box mt={2}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                        >
                            <Typography variant="subtitle2" fontWeight={600}>
                                Meta Data (Configurações extras)
                            </Typography>
                            <Button
                                startIcon={<Add />}
                                size="small"
                                onClick={() => append({ key: "", value: "" })}
                            >
                                Adicionar
                            </Button>
                        </Box>

                        {fields.map((item, index) => (
                            <Box
                                key={item.fieldId}
                                display="flex"
                                gap={1}
                                mb={1}
                                alignItems="flex-start"
                            >
                                <Controller
                                    name={`acquirer_meta_data.${index}.key`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Chave"
                                            size="small"
                                            fullWidth
                                            error={
                                                !!errors.acquirer_meta_data?.[
                                                    index
                                                ]?.key
                                            }
                                            helperText={
                                                errors.acquirer_meta_data?.[
                                                    index
                                                ]?.key?.message
                                            }
                                        />
                                    )}
                                />
                                <Controller
                                    name={`acquirer_meta_data.${index}.value`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Valor"
                                            size="small"
                                            fullWidth
                                            error={
                                                !!errors.acquirer_meta_data?.[
                                                    index
                                                ]?.value
                                            }
                                            helperText={
                                                errors.acquirer_meta_data?.[
                                                    index
                                                ]?.value?.message
                                            }
                                        />
                                    )}
                                />
                                <IconButton
                                    color="error"
                                    onClick={() => handleRemove(index)}
                                    sx={{ mt: 0.5 }}
                                >
                                    <Delete />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                        mt={2}
                    >
                        <Button onClick={handleClose} color="inherit">
                            Cancelar
                        </Button>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            loading={isSubmitting}
                            disabled={!isValid}
                        >
                            Salvar
                        </LoadingButton>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
